import { toast } from 'react-toastify';
import { get } from 'lodash-es';

import customerDataRequest, { customerDataMicroserviceRequest } from '../services/api/customer-data';
import { getAuthToken } from '../auth/authHelpers';
import { isThemeOrganization } from './theme';
import * as routes from '../constants/routes';

const loadPublicationsCallback = async (salesforceId,
    type,
    setData,
    setIsLoading = null,
    limit = null,
    setErrorText = null,
    getSlug = null) => {
    let url = `/organizations/${salesforceId}/publications?type=${type}`;
    if (limit) {
        url += `&limit=${limit}`;
    }

    if (getSlug) {
        url += '&getSlug=true';
    }

    if (salesforceId) {
        if (setIsLoading) {
            setIsLoading(true);
        }
        await customerDataRequest('get', url)
            .then((res) => {
                setData(res.data.data);
                if (setIsLoading) {
                    setIsLoading(false);
                }
            })
            .catch(() => {
                if (setIsLoading) {
                    setIsLoading(false);
                }
                const errorText = 'There was an error loading publications. Please try again in a few minutes.';
                if (setErrorText) {
                    setErrorText(errorText);
                }
                toast.error(errorText);
            });
    }
};

const loadContractsCallback = async (salesforceId,
    getAccessToken,
    setIsLoading,
    setContracts,
    fullData = false,
    singleContract = false) => {
    if (salesforceId) {
        setIsLoading(true);
        const token = await getAuthToken(getAccessToken);
        customerDataRequest('get', `organizations/${salesforceId}/contracts`, token)
            .then((res) => {
                setIsLoading(false);
                if (singleContract) {
                    if (get(res, 'data.data[0]')) { // Only first contract ID
                        setContracts(res.data.data[0].contract_id);
                    }
                } else if (fullData) { // Full contract objects
                    setContracts(res.data.data);
                } else if (get(res, 'data.data[0]')) { // List of IDs
                    setContracts(res.data.data.map((contract) => contract.contract_id));
                } else {
                    setContracts(null);
                }
            })
            .catch(() => {
                setIsLoading(false);
                if (fullData) {
                    setContracts([]);
                } else {
                    setContracts(null);
                }
                toast.error(`There was an error loading the organization's
                 contracts. Please try again in a few minutes.`);
            });
    }
};

const loadOwnersCallback = async (salesforceId,
    getAccessToken,
    setIsLoading,
    setOwners) => {
    if (salesforceId) {
        setIsLoading(true);
        const token = await getAuthToken(getAccessToken);
        await customerDataMicroserviceRequest('get', process.env.ORGANIZATION_OWNERSHIP_API_URL, `organizations/${salesforceId}/owners`, token)
            .then((res) => {
                setIsLoading(false);
                setOwners(res.data);
            })
            .catch(() => {
                setIsLoading(false);
                toast.error(`There was an error loading the organization's
                owners. Please try again in a few minutes.`);
            });
    }
};

const removeOwnerCallback = async (salesforceId, ownerId, getAccessToken, setIsLoading, handleSuccess, handleError, payload) => {
    if (salesforceId && ownerId) {
        setIsLoading(true);
        const token = await getAuthToken(getAccessToken);
        customerDataMicroserviceRequest('patch', process.env.ORGANIZATION_OWNERSHIP_API_URL, `organizations/${salesforceId}/owners/${ownerId}`, token, payload)
            .then((res) => {
                setIsLoading(false);
                handleSuccess(res.data);
            })
            .catch(() => {
                setIsLoading(false);
                handleError();
            });
    }
};

const loadOrganizationCallback = async (slug, setIsLoading, setOrganizationData, history, themeContext) => {
    setIsLoading(true);
    await customerDataRequest('get', `/organizations/slug/${slug}/`)
        .then((res) => {
            const newOrganization = res.data.data;
            if (isThemeOrganization(newOrganization, themeContext)) {
                setOrganizationData(newOrganization);
                setIsLoading(false);
            } else {
                history(`${routes.ERROR_404}`);
            }
        })
        .catch((err) => {
            setIsLoading(false);
            if (err.response && err.response.status === 404) {
                // Need to have it go to a 404 page
                history(`${routes.ERROR_404}`);
            } else {
                // Pointing all other errors to a 500 page
                history(`${routes.ERROR_500}`);
            }
        });
};

const loadOrganizationByIdCallback = async (salesforceId, setIsLoading, setOrganizationData, history, themeContext) => {
    setIsLoading(true);

    try {
        const {
            data: {
                data: organizationData,
            },
        } = await customerDataRequest('get', `/organizations/${salesforceId}`);

        if (isThemeOrganization(organizationData, themeContext)) {
            setOrganizationData(organizationData);
            setIsLoading(false);
        } else {
            history(`${routes.ERROR_404}`);
        }
    } catch (error) {
        if (error?.response && error.response?.status === 404) {
            history(`${routes.ERROR_404}`);
        } else {
            history(`${routes.ERROR_500}`);
        }
    } finally {
        setIsLoading(false);
    }
};

const addOwnerCallback = async (salesforceId, email, getAccessToken, setIsLoading, handleSuccess, handleError, isPOL = true) => {
    if (salesforceId && email) {
        setIsLoading(true);
        const token = await getAuthToken(getAccessToken);
        customerDataMicroserviceRequest('post', process.env.ORGANIZATION_OWNERSHIP_API_URL, `organizations/${salesforceId}/owners`, token, { email, isPOL })
            .then((res) => {
                setIsLoading(false);
                handleSuccess(res.data);
            })
            .catch(() => {
                setIsLoading(false);
                handleError();
            });
    }
};

const addEventCallback = async (salesforceId, event, getAccessToken) => {
    if (!salesforceId || !event) {
        return {
            success: false,
        };
    }

    const token = await getAuthToken(getAccessToken);

    if (event.id) {
        const result = await customerDataMicroserviceRequest('put', process.env.EVENT_MANAGEMENT_API_URL, `organizations/${salesforceId}/events/${event.id}`, token, event);
        if (result.status === 204) {
            return {
                success: true,
                data: { ...event, event_date: `${event.event_date.split(' ').join('T')}.000Z`, deleted_at: null },
            };
        }
    } else {
        const result = await customerDataMicroserviceRequest('post', process.env.EVENT_MANAGEMENT_API_URL, `organizations/${salesforceId}/events`, token, event);
        if (result.status === 200) {
            return {
                success: true,
                data: result.data,
            };
        }
    }

    return {
        success: false,
    };
};

const removeEventCallback = async (salesforceId, eventId, getAccessToken) => {
    if (!salesforceId || !eventId) {
        return {
            success: false,
        };
    }

    const token = await getAuthToken(getAccessToken);
    const result = await customerDataMicroserviceRequest('delete', process.env.EVENT_MANAGEMENT_API_URL, `organizations/${salesforceId}/events/${eventId}`, token);
    if (result.status === 204) {
        return {
            success: true,
        };
    }

    return {
        success: false,
    };
};

const getPdfUrlByPidCallback = async (salesforceId, pid, getAccessToken) => {
    if (!salesforceId || !pid) {
        return null;
    }

    try {
        const token = await getAuthToken(getAccessToken);
        const result = await customerDataMicroserviceRequest('get', process.env.FLIPBOOK_PDF_API_URL, `flipbook/pdf/${salesforceId}/${pid}/pdfurl`, token);

        if (result.status === 200) {
            return result.data;
        }

        return null;
    } catch (error) {
        return null;
    }
};

export {
    loadPublicationsCallback,
    loadContractsCallback,
    loadOwnersCallback,
    addOwnerCallback,
    removeOwnerCallback,
    loadOrganizationCallback,
    loadOrganizationByIdCallback,
    addEventCallback,
    removeEventCallback,
    getPdfUrlByPidCallback,
};
